import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { FormStack } from "../../generic/components/form-fields/form-stack";

import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import type { SerializedEditorState } from "lexical";
import type { FC } from "react";

export const FooterSettingsEditor: FC = () => {
  const { setValue } = useFormContext<EmailDesignSchema>();

  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

  const handleToggleFooter = useCallback(
    (checked: boolean): void => {
      if (checked) {
        const emptyFooter: SerializedEditorState = {
          root: {
            type: "root",
            version: 1,
            children: [
              {
                type: "paragraph",
                version: 0,
              },
            ],
            direction: "ltr",
            format: "left",
            indent: 0,
          },
        };
        setValue("footer", emptyFooter);
      } else {
        setOpenWarningModal(true);
      }
    },
    [setValue],
  );

  return (
    <>
      {openWarningModal && (
        <WarningModal
          open={openWarningModal}
          closeModal={() => setOpenWarningModal(false)}
          onContinue={() => {
            setValue("footer", null);
            setOpenWarningModal(false);
          }}
        />
      )}
      <FormStack>
        <CeToggle<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer"
          label="Enable Footer"
          onChange={handleToggleFooter}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_side"
          label="Side Padding"
          min={0}
          max={50}
          step={1}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_top"
          label="Top Padding"
          min={0}
          max={50}
          step={1}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_bottom"
          label="Bottom Padding"
          min={0}
          max={50}
          step={1}
        />
      </FormStack>
    </>
  );
};

const WarningModal: FC<{ open: boolean; closeModal: () => void; onContinue: () => void }> = ({
  open,
  closeModal,
  onContinue,
}) => (
  <Dialog open={open} onClose={closeModal}>
    <DialogTitle>Remove Footer?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Disabling the footer will delete its contents. Are you sure?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button
        data-analytics-id="email-design-remove-footer-dialog-cancel"
        variant="outlined"
        color="secondary"
        onClick={closeModal}
      >
        Cancel
      </Button>
      <Button
        data-analytics-id="email-design-remove-footer-dialog-continue"
        variant="outlined"
        onClick={onContinue}
      >
        Continue
      </Button>
    </DialogActions>
  </Dialog>
);
